import type { CurrencyAmount, Redacted } from '@omnipresentgroup/design-system';

import type { GetEmployeeAPIResponse } from '..';

export type EmployeeHasSupplementaryBenefitsType = 'yes' | 'no' | 'unknown';

export enum OnboardingStatusValues {
  INPROGRESS = 'In progress',
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

type EmployeeLocalOnboardingDetails = DetailKnownField | DetailUnknownField;

type DetailKnownField = {
  name: string;
  type: LODFieldType;
  value: string | number | boolean;
  label: string;
};

type DetailUnknownField = {
  type: LODFieldType;
  value: string | number | boolean;
  label?: string;
  originalRef?: string;
  labelBold?: boolean;
  title?: string;
  ref?: string;
  answerType?: TypeformAnswerType;
  fieldType?: string;
};

export type LODFieldType =
  | 'text'
  | 'date'
  | 'boolean'
  | 'file'
  | 'choice'
  | 'choices'
  | 'email'
  | 'url'
  | 'number'
  | 'payment'
  | 'checkbox';

type TypeformAnswerType =
  | 'choice'
  | 'choices'
  | 'date'
  | 'email'
  | 'url'
  | 'file_url'
  | 'number'
  | 'boolean'
  | 'text'
  | 'payment';

export enum EmployeeType {
  EOR = 'EOR',
  PEO = 'PEO',
  CONTRACTOR = 'Contractor',
  VIRTUAL_EMPLOYER = 'Virtual Employer',
}

export enum ContractStatus {
  ARCHIVED = 'archived',
  PENDING_REVIEW = 'pending-review',
  REVIEW_DONE = 'review-done',
  DRAFT = 'draft',
  APPROVED = 'approved',
  SIGNED_EMPLOYEE = 'signed:employee',
  SIGNED_BOTH_PARTIES = 'signed:both-parties',
}

export type EmployeeContract =
  | AvvokaAutomatedContract
  | ManuallyUploadedContract;

type AvvokaAutomatedContract = {
  avvokaDocumentId?: string;
  avvokaTemplateId?: string;
  contractId?: string;
  createdAt: string;
  documentId?: string;
  status: ContractStatus;
  updatedAt?: string;
  archivedAt?: string;
  version: number;
};
type ManuallyUploadedContract = {
  createdAt: string;
  documentId: string;
  status: ContractStatus;
  updatedAt?: string;
  archivedAt?: string;
  version: number;
};

const depositTypes = [
  'Advanced Payment Deposit (APD)',
  'Zero Deposit Insurance',
  'No Deposit',
  'None',
] as const;

export type DepositType = (typeof depositTypes)[number];

enum SetupFeeStructureEnum {
  PER_EMPLOYEE = 'per employee',
  PER_COUNTRY = 'per country',
}

type PayrollDetailsFields = {
  billingCurrency?: string;
  managementFee?: { amount: string | number; currency: string };
  setupFee?: { amount: string | number; currency: string };
  setupFeeStructure?: SetupFeeStructureEnum;
  hasDepositInsurance?: boolean;
  depositType?: DepositType;
  managementFeeWaiver?: {
    percentage: number;
    periodStart?: string;
    billingCycles: number;
  };
  payrollId?: string;
  zeroDepositFee?: { amount: string | number; currency: string };
  depositFee: {
    calculatedAmount: CurrencyAmount['amount'];
    convertedAmount: CurrencyAmount['amount'];
  };
  advancedPaymentDepositPercentage?: number;
  annualBaseSalary?: CurrencyAmount;
  managementFeeDiscount?: {
    percentage?: number;
    billingCycles?: number;
  };
  employeeType?: EmployeeType;
  fxFee?: { amount: string | number; currency: string };
};

export type Employee = {
  currentEmploymentId?: string;
  name: string;
  userId: string;
  managerId: string;
  profilePicture: string;
  needsRequiredFieldsFilled: boolean;
  requireTypeFormFilled: boolean;
  onboardingFormResponses: {
    typeformId: string;
    filledAt: string;
    typeformResponseId: string;
  }[];
  externalReferences: {
    id: string;
    externalRemoteId: string;
    integrationId: string;
  }[];
  id: string;
  lspId: string;
  legalEntityId?: string;
  externalId: string;
  country: {
    id: string;
    currency: string;
    name: string;
  };
  contactDetails?: {
    personalEmail: Redacted | string;
    phoneNumber: Redacted | string;
    workEmail: Redacted | string;
  };
  onboardingStatus: { status: OnboardingStatusValues };
  personalDetails: {
    salutation: string;
    firstName: string;
    lastName: string;
    dateOfBirth: Redacted | string;
    socialInsuranceNumber: {
      localName: string;
      number: Redacted | string;
    };
    maritalStatus: string;
    noOfChildren: number;
    privateAddress:
      | Redacted
      | {
          addressLine1: string;
          city: string;
          postalCode: string;
          country: string;
          addressLine2?: string;
          addressLine3?: string;
          state?: string;
        };
    citizenship: string;
    bankingDetails: {
      country: Redacted | string;
      accountHolderName: Redacted | string;
      accountNumber: Redacted | string;
      bankName?: Redacted | string;
      bicSwift: Redacted | string;
      transitNumber?: Redacted | string;
      insituteNumber?: Redacted | string;
      sortCode?: Redacted | string;
      routingNumber: Redacted | string;
      iban: Redacted | string;
      branchName: Redacted | string;
    };
    middleNames?: string;
    gender?: string;
    pronouns?: string;
    identificationDocument?: {
      type: string;
      link: string;
    };
  };
  employmentDetails: {
    locationOfWork:
      | Redacted
      | {
          type: string;
          address: {
            addressLine1: string;
            city: string;
            postalCode: string;
            country: string;
            addressLine2?: string;
            state?: string;
          };
        };
    jobTitle: string;
    startDate: string;
    firstPayDate: string;
    jobDescription: string;
    additionalCompensation?: Redacted | string;
    hoursOfWork: Redacted | number;
    rightToWorkCheckCompleted: boolean;
    probationaryPeriod: {
      length: Redacted | number;
      timeUnit: string;
    };
    annualBaseSalary: {
      amount: Redacted | number;
      currency: string;
    };
    hasSupplementaryBenefits: EmployeeHasSupplementaryBenefitsType;
    leave: {
      type: string;
      value: number;
    }[];
    salaryHistory: {
      reason: string;
      amount: number;
      notes?: string;
      requestedAt: string;
      requestedByAuthId: string;
      jobTitle: string;
      hoursOfWork: number;
      currency: string;
      jobDescription: string;
      effectiveDate: string;
    }[];
  };
  employeeType: EmployeeType;
  createdAt: string;
  updatedAt: string;
  localOnboardingDetails: EmployeeLocalOnboardingDetails[];
  // TODO: more accurate types for this
  contractQuestionnaire: {
    templateId: number;
    body: {
      [key: string]: {
        position: number;
        title: string;
        type: string;
        value: string | number;
        hint?: string;
      };
    };
  };
  updatedByAuthId: string;
  contracts?: EmployeeContract[];
  newUpdatedBy: string;
  newUpdatedAt: string;
  payrollDetails: PayrollDetailsFields;
  companyName: string;
  companySettings?: {
    disableContractAutomation: boolean;
    enforceMFA: boolean;
    isTimeOffEnabled: boolean;
  };
  adminNotes?: string;
  employmentInformation?: string;
  temporaryPassword?: string;
  seenPrivacyPolicy?: string;
};

export type SimplifiedEmployee = {
  id: string;
  userId: string;
  name: string;
  email: Redacted | string;
  country: string;
  currency: CurrencyAmount['currency'];
  annualBaseSalary: Redacted | CurrencyAmount['amount'];
  jobTitle: string;
  startDate: string;
  onboardingStatus: OnboardingStatusValues;
  managerId: string;
  lspId?: string;
  nextCutoffDate?: string;
};

export enum CombinedEmployeeType {
  EMPLOYEE = 'Employee',
  PLANNED_HIRE = 'Planned hire',
}

// Experimental type for the merge of employees and seats
export type CombinedEmployee = {
  id: string;
  name: string;
  jobTitle: string;
  country: string;
  startDate: string;
  nextCutoffDate?: string;
  annualBaseSalary: CurrencyAmount;
  onboardingStatus: string;
  type: CombinedEmployeeType;
};

export type GetCompanyEmployeesAPIResponse = {
  employees: GetEmployeeAPIResponse[];
};
